import { useState, useEffect, useCallback, type KeyboardEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { api, ApiError } from '../services/api';
import type { BrandStrategy as BrandStrategyType, Envergure, LocationScope, GuardrailViolation } from '@traaker/shared';

const BRAND_STRATEGY_LIMITS = {
  industry: 80,
  clientPersonas: 500,
  geographicFocus: 200,
  description: 800,
  aliases: { maxItems: 10, maxCharsPerItem: 80 },
  differentiators: { maxItems: 10, maxCharsPerItem: 150 },
  scenarios: { maxItems: 15, maxCharsPerItem: 200 },
  keywords: { maxItems: 30, maxCharsPerItem: 60 },
  customerQuestions: { maxItems: 20, maxCharsPerItem: 200 },
  customerObjections: { maxItems: 20, maxCharsPerItem: 200 },
  avoidTopics: { maxItems: 20, maxCharsPerItem: 150 },
  targetLanguages: { maxItems: 10, maxCharsPerItem: 10 },
  existingAssets: {
    wikipediaUrl: 500,
    pressKitUrl: 500,
    notableMentions: { maxItems: 20, maxCharsPerItem: 300 },
  },
} as const;

const SCOPE_LABELS: Record<LocationScope, string> = {
  worldwide: 'Mondial',
  national: 'National',
  city: 'Ville',
  address: 'Adresse',
};

const BUSINESS_MODEL_LABELS: Record<string, string> = {
  b2b: 'B2B',
  b2c: 'B2C',
  both: 'B2B & B2C',
};

const ENVERGURE_LABELS: Record<Envergure, string> = {
  'hyper-local': 'Hyper-locale (1 ville / quartier)',
  'multi-local': 'Multi-locale (plusieurs villes)',
  'mono-country': 'Mono-pays (nationale)',
  'multi-country': 'Multi-pays',
  global: 'Mondiale',
};

const TIER_LABELS: Record<string, string> = {
  lite: 'Lite',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

const ZONE_LIMITS: Record<string, number> = { lite: 1, pro: 5, enterprise: -1 };

export default function BrandStrategy() {
  const { activeBrand, brands, refreshBrands } = useBrand();
  const { user, tenant } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const tier = tenant?.tier || 'lite';
  const zoneLimit = tenant?.settings?.quotaOverrides?.zones ?? (ZONE_LIMITS[tier] ?? 1);
  const totalZones = brands.reduce((sum, b) => sum + b.locations.length, 0);
  const zonesUnlimited = zoneLimit === -1;
  const zonesLimitReached = !zonesUnlimited && totalZones >= zoneLimit;

  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [newLocationLabel, setNewLocationLabel] = useState('');
  const [newLocationZone, setNewLocationZone] = useState('');
  const [newLocationScope, setNewLocationScope] = useState<LocationScope | ''>('');
  const [locationSubmitting, setLocationSubmitting] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const openLocationDialog = () => {
    setNewLocationLabel('');
    setNewLocationZone('');
    setNewLocationScope('');
    setLocationError(null);
    setLocationDialogOpen(true);
  };

  const submitNewLocation = async () => {
    if (!activeBrand) return;
    setLocationSubmitting(true);
    setLocationError(null);
    try {
      await api.post(`/brands/${activeBrand.brandId}/locations`, {
        label: newLocationLabel,
        zone: newLocationZone || newLocationLabel,
        ...(newLocationScope && { scope: newLocationScope }),
      });
      await refreshBrands();
      setLocationDialogOpen(false);
    } catch (err: any) {
      setLocationError(err.message || 'Erreur lors de l\'ajout');
    } finally {
      setLocationSubmitting(false);
    }
  };

  const deleteLocation = async (locationId: string) => {
    if (!activeBrand) return;
    try {
      await api.delete(`/brands/${activeBrand.brandId}/locations/${locationId}`);
      await refreshBrands();
    } catch {
      // silent; user will see stale state and can retry
    }
  };

  const saveScopeChange = async (locationId: string, scope: LocationScope) => {
    if (!activeBrand) return;
    try {
      await api.patch(`/brands/${activeBrand.brandId}/locations/${locationId}`, { scope });
      await refreshBrands();
    } catch {
      // silent — user sees stale value and can retry
    }
  };

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Strategy fields
  const [aliases, setAliases] = useState<string[]>([]);
  const [industry, setIndustry] = useState('');
  const [clientPersonas, setClientPersonas] = useState('');
  const [businessModel, setBusinessModel] = useState<'b2b' | 'b2c' | 'both' | ''>('');
  const [geographicFocus, setGeographicFocus] = useState('');
  const [description, setDescription] = useState('');
  const [differentiators, setDifferentiators] = useState<string[]>([]);
  const [scenarios, setScenarios] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [customerQuestions, setCustomerQuestions] = useState<string[]>([]);
  const [customerObjections, setCustomerObjections] = useState<string[]>([]);
  const [avoidTopics, setAvoidTopics] = useState<string[]>([]);
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
  const [envergure, setEnvergure] = useState<Envergure | ''>('');
  const [wikipediaUrl, setWikipediaUrl] = useState('');
  const [pressKitUrl, setPressKitUrl] = useState('');
  const [notableMentions, setNotableMentions] = useState<string[]>([]);

  // Chip input buffers
  const [aliasInput, setAliasInput] = useState('');
  const [diffInput, setDiffInput] = useState('');
  const [scenarioInput, setScenarioInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [questionInput, setQuestionInput] = useState('');
  const [objectionInput, setObjectionInput] = useState('');
  const [avoidInput, setAvoidInput] = useState('');
  const [languageInput, setLanguageInput] = useState('');
  const [mentionInput, setMentionInput] = useState('');

  const loadFromBrand = useCallback(() => {
    if (!activeBrand) return;
    const s = activeBrand.strategy;
    setAliases(s?.aliases || []);
    setIndustry(s?.industry || '');
    setClientPersonas(s?.clientPersonas || '');
    setBusinessModel(s?.businessModel || '');
    setGeographicFocus(s?.geographicFocus || '');
    setDescription(s?.description || '');
    setDifferentiators(s?.differentiators || []);
    setScenarios(s?.scenarios || []);
    setKeywords(s?.keywords || []);
    setCustomerQuestions(s?.customerQuestions || []);
    setCustomerObjections(s?.customerObjections || []);
    setAvoidTopics(s?.avoidTopics || []);
    setTargetLanguages(s?.targetLanguages || []);
    setEnvergure(s?.envergure || '');
    setWikipediaUrl(s?.existingAssets?.wikipediaUrl || '');
    setPressKitUrl(s?.existingAssets?.pressKitUrl || '');
    setNotableMentions(s?.existingAssets?.notableMentions || []);
  }, [activeBrand]);

  useEffect(() => {
    loadFromBrand();
    setEditing(false);
    setError(null);
    setFieldErrors({});
  }, [loadFromBrand]);

  const handleCancel = () => {
    loadFromBrand();
    setEditing(false);
    setError(null);
    setFieldErrors({});
  };

  const handleSave = async () => {
    if (!activeBrand) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});

    const hasAssets = wikipediaUrl || pressKitUrl || notableMentions.length > 0;
    const payload: Partial<BrandStrategyType> = {
      aliases,
      industry: industry || undefined,
      clientPersonas: clientPersonas || undefined,
      businessModel: businessModel || undefined,
      geographicFocus: geographicFocus || undefined,
      description: description || undefined,
      differentiators,
      scenarios,
      keywords,
      customerQuestions,
      customerObjections,
      avoidTopics,
      targetLanguages,
      envergure: envergure || undefined,
      existingAssets: hasAssets
        ? {
            wikipediaUrl: wikipediaUrl || undefined,
            pressKitUrl: pressKitUrl || undefined,
            notableMentions: notableMentions.length > 0 ? notableMentions : undefined,
          }
        : undefined,
    };

    try {
      await api.patch(`/brands/${activeBrand.brandId}/strategy`, payload);
      await refreshBrands();
      setEditing(false);
    } catch (err: any) {
      if (err instanceof ApiError && err.details) {
        const violations = err.details as GuardrailViolation[];
        const errors: Record<string, string> = {};
        violations.forEach((v) => {
          // Map array field violations back to parent field
          const field = v.field.replace(/\[\d+\]$/, '');
          errors[field] = v.message;
        });
        setFieldErrors(errors);
        setError('Certains champs contiennent du contenu non autorise.');
      } else {
        setError(err.message || 'Erreur lors de la sauvegarde');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!activeBrand) return;
    setValidating(true);
    setError(null);
    setInfo(null);
    try {
      await api.post(`/brands/${activeBrand.brandId}/strategy/validate`, {});
      await refreshBrands();
      setInfo(
        'Brand Knowledge validée. Vous pouvez maintenant générer la GEO Strategy.',
      );
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  const handleChipAdd = (
    value: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
    cfg?: { maxItems: number; maxCharsPerItem: number },
  ) => {
    const trimmed = value.trim();
    if (cfg) {
      if (list.length >= cfg.maxItems) {
        setInput('');
        return;
      }
      if (trimmed.length > cfg.maxCharsPerItem) return;
    }
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setInput('');
  };

  const handleChipKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    value: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
    cfg?: { maxItems: number; maxCharsPerItem: number },
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleChipAdd(value, list, setList, setInput, cfg);
    }
  };

  const chipHelper = (
    list: string[],
    cfg: { maxItems: number; maxCharsPerItem: number },
    fieldError?: string,
  ) =>
    fieldError ||
    `${list.length} / ${cfg.maxItems} items · max ${cfg.maxCharsPerItem} car. par item`;

  if (!activeBrand) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          Brand Knowledge
        </Typography>
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Selectionnez ou creez une marque dans les parametres.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const hasStrategy = activeBrand.strategy && Object.keys(activeBrand.strategy).length > 0;
  const brandMeta = activeBrand.strategyMeta;
  const brandStatus = brandMeta?.status;
  const STATUS_CHIPS: Record<string, { label: string; color: 'default' | 'success' | 'warning'; icon?: React.ReactElement }> = {
    draft: { label: 'Brouillon', color: 'default' },
    validated: { label: 'Validée', color: 'success', icon: <VerifiedIcon fontSize="small" /> },
    stale: { label: 'Obsolète — à revalider', color: 'warning' },
  };
  const statusChip = brandStatus ? STATUS_CHIPS[brandStatus] : null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Brand Knowledge
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeBrand.name} {activeBrand.url && `— ${activeBrand.url}`}
          </Typography>
          {statusChip && (
            <Box sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={`Statut : ${statusChip.label}`}
                color={statusChip.color}
                icon={statusChip.icon}
                variant="outlined"
              />
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {isAdmin && !editing && hasStrategy && brandStatus !== 'validated' && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleValidate}
              disabled={validating}
            >
              {validating ? 'Validation…' : 'Valider'}
            </Button>
          )}
          {isAdmin && !editing && (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
              Modifier
            </Button>
          )}
          {isAdmin && editing && (
            <>
              <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleCancel}>
                Annuler
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
                Enregistrer
              </Button>
            </>
          )}
        </Box>
      </Box>

      {!hasStrategy && !editing && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Aucune strategie configuree pour cette marque. {isAdmin ? 'Cliquez sur "Modifier" pour commencer.' : 'Un administrateur doit configurer la strategie.'}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}
      {hasStrategy && brandStatus === 'stale' && !editing && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          La Brand Knowledge a changé depuis sa dernière validation. Cliquez sur « Valider »
          pour la réactiver — les agents n'utilisent que des stratégies validées.
        </Alert>
      )}
      {hasStrategy && !brandStatus && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Cette Brand Knowledge n'a pas encore été validée. Cliquez sur « Valider » pour la figer
          et permettre la génération de la GEO Strategy.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Identity */}
        <Grid size={12}>
          <SectionCard title="Identite" usedBy="Earned Media, Brand Perception, Veille">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label="Nom de la marque" />
                <Typography>{activeBrand.name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label="Site web" />
                <Typography>{activeBrand.url || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label="Secteur d'activite" />
                {editing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.industry))}
                    placeholder="Ex: SaaS, E-commerce, Conseil..."
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.industry }}
                    helperText={`${industry.length} / ${BRAND_STRATEGY_LIMITS.industry}`}
                  />
                ) : (
                  <Typography>{industry || '—'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label="Aliases / noms alternatifs" />
                {editing ? (
                  <Box>
                    <ChipList items={aliases} onDelete={(i) => setAliases(aliases.filter((_, idx) => idx !== i))} />
                    <TextField
                      fullWidth
                      size="small"
                      value={aliasInput}
                      onChange={(e) =>
                        setAliasInput(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.aliases.maxCharsPerItem))
                      }
                      onKeyDown={(e) =>
                        handleChipKeyDown(
                          e,
                          aliasInput,
                          aliases,
                          setAliases,
                          setAliasInput,
                          BRAND_STRATEGY_LIMITS.aliases,
                        )
                      }
                      placeholder="Taper puis Entree pour ajouter"
                      inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.aliases.maxCharsPerItem }}
                      helperText={chipHelper(aliases, BRAND_STRATEGY_LIMITS.aliases)}
                      disabled={aliases.length >= BRAND_STRATEGY_LIMITS.aliases.maxItems}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ) : (
                  <ChipList items={aliases} />
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Target Market */}
        <Grid size={12}>
          <SectionCard title="Marche cible" usedBy="Brand Perception (génération de requêtes)">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label="Personas clients" />
                {editing ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    value={clientPersonas}
                    onChange={(e) =>
                      setClientPersonas(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.clientPersonas))
                    }
                    placeholder="Decrivez vos clients types..."
                    error={!!fieldErrors.clientPersonas}
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.clientPersonas }}
                    helperText={
                      fieldErrors.clientPersonas ||
                      `${clientPersonas.length} / ${BRAND_STRATEGY_LIMITS.clientPersonas}`
                    }
                  />
                ) : (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{clientPersonas || '—'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FieldLabel label="Modele business" />
                {editing ? (
                  <Select
                    fullWidth
                    size="small"
                    value={businessModel}
                    onChange={(e) => setBusinessModel(e.target.value as any)}
                    displayEmpty
                  >
                    <MenuItem value="">Non defini</MenuItem>
                    <MenuItem value="b2b">B2B</MenuItem>
                    <MenuItem value="b2c">B2C</MenuItem>
                    <MenuItem value="both">B2B & B2C</MenuItem>
                  </Select>
                ) : (
                  <Typography>{businessModel ? BUSINESS_MODEL_LABELS[businessModel] : '—'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FieldLabel label="Focus geographique" />
                {editing ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    value={geographicFocus}
                    onChange={(e) =>
                      setGeographicFocus(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.geographicFocus))
                    }
                    placeholder="France, Europe, Mondial..."
                    error={!!fieldErrors.geographicFocus}
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.geographicFocus }}
                    helperText={
                      fieldErrors.geographicFocus ||
                      `${geographicFocus.length} / ${BRAND_STRATEGY_LIMITS.geographicFocus}`
                    }
                  />
                ) : (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{geographicFocus || '—'}</Typography>
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Value Proposition */}
        <Grid size={12}>
          <SectionCard title="Proposition de valeur" usedBy="Earned Media, Brand Perception, Veille">
            <Grid container spacing={2}>
              <Grid size={12}>
                <FieldLabel label="Description" />
                {editing ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    value={description}
                    onChange={(e) =>
                      setDescription(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.description))
                    }
                    placeholder="Que fait votre entreprise et quelle valeur apporte-t-elle ?"
                    error={!!fieldErrors.description}
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.description }}
                    helperText={
                      fieldErrors.description ||
                      `${description.length} / ${BRAND_STRATEGY_LIMITS.description}`
                    }
                  />
                ) : (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{description || '—'}</Typography>
                )}
              </Grid>
              <Grid size={12}>
                <FieldLabel label="Differenciateurs cles" />
                {editing ? (
                  <Box>
                    <ChipList items={differentiators} onDelete={(i) => setDifferentiators(differentiators.filter((_, idx) => idx !== i))} />
                    <TextField
                      fullWidth
                      size="small"
                      value={diffInput}
                      onChange={(e) =>
                        setDiffInput(
                          e.target.value.slice(0, BRAND_STRATEGY_LIMITS.differentiators.maxCharsPerItem),
                        )
                      }
                      onKeyDown={(e) =>
                        handleChipKeyDown(
                          e,
                          diffInput,
                          differentiators,
                          setDifferentiators,
                          setDiffInput,
                          BRAND_STRATEGY_LIMITS.differentiators,
                        )
                      }
                      placeholder="Taper puis Entree pour ajouter"
                      error={!!fieldErrors.differentiators}
                      inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.differentiators.maxCharsPerItem }}
                      helperText={chipHelper(
                        differentiators,
                        BRAND_STRATEGY_LIMITS.differentiators,
                        fieldErrors.differentiators,
                      )}
                      disabled={differentiators.length >= BRAND_STRATEGY_LIMITS.differentiators.maxItems}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ) : (
                  <ChipList items={differentiators} />
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Sales Plays */}
        <Grid size={12}>
          <SectionCard title="Cas d'usage / Sales Plays" usedBy="Brand Perception, Veille">
            <FieldLabel label="Scenarios a surveiller" />
            {editing ? (
              <Box>
                <ChipList items={scenarios} onDelete={(i) => setScenarios(scenarios.filter((_, idx) => idx !== i))} />
                <TextField
                  fullWidth
                  size="small"
                  value={scenarioInput}
                  onChange={(e) =>
                    setScenarioInput(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.scenarios.maxCharsPerItem))
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      scenarioInput,
                      scenarios,
                      setScenarios,
                      setScenarioInput,
                      BRAND_STRATEGY_LIMITS.scenarios,
                    )
                  }
                  placeholder="Ex: Migration cloud pour PME — Taper puis Entree"
                  error={!!fieldErrors.scenarios}
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.scenarios.maxCharsPerItem }}
                  helperText={chipHelper(scenarios, BRAND_STRATEGY_LIMITS.scenarios, fieldErrors.scenarios)}
                  disabled={scenarios.length >= BRAND_STRATEGY_LIMITS.scenarios.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={scenarios} />
            )}
          </SectionCard>
        </Grid>

        {/* Topics to Own */}
        <Grid size={12}>
          <SectionCard title="Sujets d'autorite" usedBy="Earned Media, Brand Perception, Veille">
            <FieldLabel label="Mots-cles et themes" />
            {editing ? (
              <Box>
                <ChipList items={keywords} onDelete={(i) => setKeywords(keywords.filter((_, idx) => idx !== i))} />
                <TextField
                  fullWidth
                  size="small"
                  value={keywordInput}
                  onChange={(e) =>
                    setKeywordInput(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.keywords.maxCharsPerItem))
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      keywordInput,
                      keywords,
                      setKeywords,
                      setKeywordInput,
                      BRAND_STRATEGY_LIMITS.keywords,
                    )
                  }
                  placeholder="Ex: GEO, optimisation IA — Taper puis Entree"
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.keywords.maxCharsPerItem }}
                  helperText={chipHelper(keywords, BRAND_STRATEGY_LIMITS.keywords)}
                  disabled={keywords.length >= BRAND_STRATEGY_LIMITS.keywords.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={keywords} />
            )}
          </SectionCard>
        </Grid>

        {/* Customer Questions — pre-purchase */}
        <Grid size={12}>
          <SectionCard
            title="Questions avant achat"
            usedBy="Brand Perception (Brand Referral — recommandations), Veille"
          >
            <FieldLabel label="Ce que les prospects demandent à un moteur IA avant de choisir un prestataire" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Questions neutres, sans a priori sur votre marque. Ex : « Combien coûte une migration cloud pour une PME ? », « Quel délai pour refondre un site e-commerce ? », « Quelles certifications chercher chez un prestataire AWS ? »
            </Typography>
            {editing ? (
              <Box>
                <ChipList
                  items={customerQuestions}
                  onDelete={(i) => setCustomerQuestions(customerQuestions.filter((_, idx) => idx !== i))}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={questionInput}
                  onChange={(e) =>
                    setQuestionInput(
                      e.target.value.slice(0, BRAND_STRATEGY_LIMITS.customerQuestions.maxCharsPerItem),
                    )
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      questionInput,
                      customerQuestions,
                      setCustomerQuestions,
                      setQuestionInput,
                      BRAND_STRATEGY_LIMITS.customerQuestions,
                    )
                  }
                  placeholder="Ex: Combien coûte... ? — Entrée pour ajouter"
                  error={!!fieldErrors.customerQuestions}
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.customerQuestions.maxCharsPerItem }}
                  helperText={chipHelper(
                    customerQuestions,
                    BRAND_STRATEGY_LIMITS.customerQuestions,
                    fieldErrors.customerQuestions,
                  )}
                  disabled={customerQuestions.length >= BRAND_STRATEGY_LIMITS.customerQuestions.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={customerQuestions} />
            )}
          </SectionCard>
        </Grid>

        {/* Customer Objections — post-awareness blockers */}
        <Grid size={12}>
          <SectionCard
            title="Objections des prospects"
            usedBy="Earned Media (validation), Brand Perception (signaux négatifs)"
          >
            <FieldLabel label="Blocages ou a priori qu'un prospect exprime au sujet de votre marque" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Critiques, doutes ou préjugés que vous entendez en rendez-vous commercial. Ex : « Vous n'avez pas la certification ISO 27001 », « Je travaille qu'avec les grands cabinets », « Vous avez mauvaise réputation en ligne », « Je ne vous connais pas, avez-vous des références ? »
            </Typography>
            {editing ? (
              <Box>
                <ChipList
                  items={customerObjections}
                  onDelete={(i) => setCustomerObjections(customerObjections.filter((_, idx) => idx !== i))}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={objectionInput}
                  onChange={(e) =>
                    setObjectionInput(
                      e.target.value.slice(0, BRAND_STRATEGY_LIMITS.customerObjections.maxCharsPerItem),
                    )
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      objectionInput,
                      customerObjections,
                      setCustomerObjections,
                      setObjectionInput,
                      BRAND_STRATEGY_LIMITS.customerObjections,
                    )
                  }
                  placeholder="Ex: Pas de certification X — Entrée pour ajouter"
                  error={!!fieldErrors.customerObjections}
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.customerObjections.maxCharsPerItem }}
                  helperText={chipHelper(
                    customerObjections,
                    BRAND_STRATEGY_LIMITS.customerObjections,
                    fieldErrors.customerObjections,
                  )}
                  disabled={customerObjections.length >= BRAND_STRATEGY_LIMITS.customerObjections.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={customerObjections} />
            )}
          </SectionCard>
        </Grid>

        {/* Avoid Topics */}
        <Grid size={12}>
          <SectionCard
            title="Sujets à éviter"
            usedBy="Earned Media (filtrage), Brand Perception (signaux négatifs)"
          >
            <FieldLabel label="Associations dont la marque ne veut pas (sujets sensibles, anciennes polémiques)" />
            {editing ? (
              <Box>
                <ChipList
                  items={avoidTopics}
                  onDelete={(i) => setAvoidTopics(avoidTopics.filter((_, idx) => idx !== i))}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={avoidInput}
                  onChange={(e) =>
                    setAvoidInput(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.avoidTopics.maxCharsPerItem))
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      avoidInput,
                      avoidTopics,
                      setAvoidTopics,
                      setAvoidInput,
                      BRAND_STRATEGY_LIMITS.avoidTopics,
                    )
                  }
                  placeholder="Ex: sujet polémique, concurrent à ne pas nommer — Entrée pour ajouter"
                  error={!!fieldErrors.avoidTopics}
                  inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.avoidTopics.maxCharsPerItem }}
                  helperText={chipHelper(avoidTopics, BRAND_STRATEGY_LIMITS.avoidTopics, fieldErrors.avoidTopics)}
                  disabled={avoidTopics.length >= BRAND_STRATEGY_LIMITS.avoidTopics.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={avoidTopics} />
            )}
          </SectionCard>
        </Grid>

        {/* Target Languages + Maturity */}
        <Grid size={12}>
          <SectionCard title="Langues & envergure" usedBy="Brand Perception, Earned Media">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label="Langues cibles pour les audits" />
                {editing ? (
                  <Box>
                    <ChipList
                      items={targetLanguages}
                      onDelete={(i) => setTargetLanguages(targetLanguages.filter((_, idx) => idx !== i))}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      value={languageInput}
                      onChange={(e) =>
                        setLanguageInput(
                          e.target.value.slice(0, BRAND_STRATEGY_LIMITS.targetLanguages.maxCharsPerItem),
                        )
                      }
                      onKeyDown={(e) =>
                        handleChipKeyDown(
                          e,
                          languageInput,
                          targetLanguages,
                          setTargetLanguages,
                          setLanguageInput,
                          BRAND_STRATEGY_LIMITS.targetLanguages,
                        )
                      }
                      placeholder="Ex: fr, en, de — Entrée pour ajouter"
                      inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.targetLanguages.maxCharsPerItem }}
                      helperText={chipHelper(targetLanguages, BRAND_STRATEGY_LIMITS.targetLanguages)}
                      disabled={targetLanguages.length >= BRAND_STRATEGY_LIMITS.targetLanguages.maxItems}
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Si vide, la langue est déduite des localisations.
                    </Typography>
                  </Box>
                ) : (
                  <ChipList items={targetLanguages} />
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label="Envergure actuelle" />
                {editing ? (
                  <>
                    <Select
                      fullWidth
                      size="small"
                      value={envergure}
                      onChange={(e) => setEnvergure(e.target.value as Envergure | '')}
                      displayEmpty
                    >
                      <MenuItem value="">Non définie</MenuItem>
                      {Object.entries(ENVERGURE_LABELS).map(([val, label]) => (
                        <MenuItem key={val} value={val}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Empreinte géographique actuelle (pas l'ambition).
                    </Typography>
                  </>
                ) : (
                  <Typography>{envergure ? ENVERGURE_LABELS[envergure] : '—'}</Typography>
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Existing Assets */}
        <Grid size={12}>
          <SectionCard title="Actifs de notoriété existants" usedBy="Earned Media (sources fiables)">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label="Page Wikipédia (URL)" />
                {editing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={wikipediaUrl}
                    onChange={(e) =>
                      setWikipediaUrl(
                        e.target.value.slice(0, BRAND_STRATEGY_LIMITS.existingAssets.wikipediaUrl),
                      )
                    }
                    placeholder="https://fr.wikipedia.org/wiki/..."
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.existingAssets.wikipediaUrl }}
                  />
                ) : (
                  <Typography sx={{ wordBreak: 'break-all' }}>{wikipediaUrl || '—'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel label="Kit presse (URL)" />
                {editing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={pressKitUrl}
                    onChange={(e) =>
                      setPressKitUrl(e.target.value.slice(0, BRAND_STRATEGY_LIMITS.existingAssets.pressKitUrl))
                    }
                    placeholder="https://..."
                    inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.existingAssets.pressKitUrl }}
                  />
                ) : (
                  <Typography sx={{ wordBreak: 'break-all' }}>{pressKitUrl || '—'}</Typography>
                )}
              </Grid>
              <Grid size={12}>
                <FieldLabel label="Mentions presse notables" />
                {editing ? (
                  <Box>
                    <ChipList
                      items={notableMentions}
                      onDelete={(i) => setNotableMentions(notableMentions.filter((_, idx) => idx !== i))}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      value={mentionInput}
                      onChange={(e) =>
                        setMentionInput(
                          e.target.value.slice(
                            0,
                            BRAND_STRATEGY_LIMITS.existingAssets.notableMentions.maxCharsPerItem,
                          ),
                        )
                      }
                      onKeyDown={(e) =>
                        handleChipKeyDown(
                          e,
                          mentionInput,
                          notableMentions,
                          setNotableMentions,
                          setMentionInput,
                          BRAND_STRATEGY_LIMITS.existingAssets.notableMentions,
                        )
                      }
                      placeholder="URL ou nom de source — Entrée pour ajouter"
                      error={!!fieldErrors['existingAssets.notableMentions']}
                      inputProps={{
                        maxLength: BRAND_STRATEGY_LIMITS.existingAssets.notableMentions.maxCharsPerItem,
                      }}
                      helperText={chipHelper(
                        notableMentions,
                        BRAND_STRATEGY_LIMITS.existingAssets.notableMentions,
                        fieldErrors['existingAssets.notableMentions'],
                      )}
                      disabled={
                        notableMentions.length >=
                        BRAND_STRATEGY_LIMITS.existingAssets.notableMentions.maxItems
                      }
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ) : (
                  <ChipList items={notableMentions} />
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Localisations */}
        <Grid size={12}>
          <SectionCard
            title="Localisations"
            usedBy="Tous les audits (contexte géographique)"
            headerRight={
              <Chip
                size="small"
                label={
                  zonesUnlimited
                    ? `${totalZones} — illimité`
                    : `${totalZones} / ${zoneLimit} — plan ${TIER_LABELS[tier]}`
                }
                color={zonesLimitReached ? 'warning' : 'default'}
                variant="outlined"
              />
            }
          >
            {activeBrand.locations.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Aucune localisation configurée. Ajoutez au moins une zone pour que les audits
                tiennent compte du contexte géographique.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {activeBrand.locations.map((loc) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={loc.locationId}>
                    <Card variant="outlined" sx={{ p: 2, position: 'relative' }}>
                      {isAdmin && (
                        <Tooltip title="Supprimer">
                          <Button
                            size="small"
                            onClick={() => deleteLocation(loc.locationId)}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              minWidth: 0,
                              p: 0.5,
                              color: 'text.secondary',
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                      )}
                      <Typography fontWeight={600} sx={{ pr: 4 }}>
                        {loc.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {loc.zone}
                      </Typography>
                      {isAdmin ? (
                        <Select
                          fullWidth
                          size="small"
                          value={loc.scope || ''}
                          onChange={(e) =>
                            saveScopeChange(loc.locationId, e.target.value as LocationScope)
                          }
                          displayEmpty
                        >
                          <MenuItem value="">Non défini</MenuItem>
                          {Object.entries(SCOPE_LABELS).map(([val, label]) => (
                            <MenuItem key={val} value={val}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Chip
                          size="small"
                          label={SCOPE_LABELS[loc.scope as LocationScope] || 'Non défini'}
                          variant="outlined"
                        />
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            {isAdmin && (
              <Box sx={{ mt: 2 }}>
                <Tooltip
                  title={
                    zonesLimitReached
                      ? `Limite du plan ${TIER_LABELS[tier]} atteinte — passez au plan supérieur`
                      : ''
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={openLocationDialog}
                      disabled={zonesLimitReached}
                    >
                      Ajouter une localisation
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* Add location dialog */}
      <Dialog open={locationDialogOpen} onClose={() => setLocationDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ajouter une localisation</DialogTitle>
        <DialogContent>
          {locationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {locationError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Libellé"
              placeholder="Ex: Paris, Siège France..."
              value={newLocationLabel}
              onChange={(e) => setNewLocationLabel(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Zone géographique"
              placeholder="Ex: Paris, France"
              value={newLocationZone}
              onChange={(e) => setNewLocationZone(e.target.value)}
              helperText="Si vide, le libellé sera utilisé."
              fullWidth
              size="small"
            />
            <Select
              value={newLocationScope}
              onChange={(e) => setNewLocationScope(e.target.value as LocationScope | '')}
              displayEmpty
              fullWidth
              size="small"
            >
              <MenuItem value="">Portée (optionnel)</MenuItem>
              {Object.entries(SCOPE_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialogOpen(false)} disabled={locationSubmitting}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={submitNewLocation}
            disabled={!newLocationLabel.trim() || locationSubmitting}
          >
            {locationSubmitting ? 'Ajout…' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Saving overlay */}
      {saving && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: '#070B14CC',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.primary">
            Analyse et sauvegarde en cours...
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Helper components ──

function SectionCard({
  title,
  usedBy,
  headerRight,
  children,
}: {
  title: string;
  usedBy?: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
            {usedBy && (
              <Typography variant="caption" color="text.secondary">
                Utilisé par : {usedBy}
              </Typography>
            )}
          </Box>
          {headerRight}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function FieldLabel({ label }: { label: string }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      {label}
    </Typography>
  );
}

function ChipList({
  items,
  onDelete,
}: {
  items: string[];
  onDelete?: (index: number) => void;
}) {
  if (items.length === 0 && !onDelete) {
    return <Typography>—</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {items.map((item, i) => (
        <Chip
          key={`${item}-${i}`}
          label={item}
          size="small"
          onDelete={onDelete ? () => onDelete(i) : undefined}
        />
      ))}
    </Box>
  );
}
